import { Preferences } from "@/lib/capacitor/preferences";
import { logger } from "@/logger";

const STORAGE_KEY = "calendar-link";

/**
 * De sleutel waaronder de koppeling in de query-cache staat.
 *
 * Hij hoort hier en niet bij het scherm dat hem het vaakst gebruikt: inmiddels vraagt ook de
 * instelkaart op Vandaag of er al een agenda gekoppeld is, en die kan niet bij een component van de
 * instellingen langs voor een constante.
 */
export const CALENDAR_LINK_QUERY_KEY = ["calendar-link"] as const;

/**
 * Welke agenda van het toestel we bijhouden.
 *
 * Bewust géén lijst van aangemaakte afspraken: welke afspraken van ons zijn staat in de agenda
 * zelf, in de handtekening (`parseShiftMarker`). Een tweede administratie hiernaast zou na een
 * herinstallatie of een handmatig verwijderde afspraak uit de pas gaan lopen, en dan gaan we
 * duplicaten maken of afspraken van iemand anders opruimen.
 */
export interface CalendarLink {
  calendarId: string;
  calendarTitle: string;
  /** ISO-tijdstip van de laatste geslaagde synchronisatie. */
  lastSyncedAt: string | null;
}

function isCalendarLink(value: unknown): value is CalendarLink {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<CalendarLink>;
  return typeof candidate.calendarId === "string" && typeof candidate.calendarTitle === "string";
}

export async function readCalendarLink(): Promise<CalendarLink | null> {
  const { value } = await Preferences.get(STORAGE_KEY);
  if (!value) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    return isCalendarLink(parsed) ? parsed : null;
  } catch (error) {
    // Onleesbare opslag is geen reden om de pagina te laten crashen; opnieuw koppelen herstelt het.
    logger.error("Agendakoppeling uit opslag lezen mislukt", { error });
    return null;
  }
}

export async function writeCalendarLink(link: CalendarLink): Promise<void> {
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(link) });
}

export async function clearCalendarLink(): Promise<void> {
  await Preferences.remove(STORAGE_KEY);
}
